import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const capgeminiCaseStudy: ContentObject = {
    id: 'capgemini-genai-legacy-java',
    kind: 'system',
    type: 'case-study',
    name: 'AI-Powered Automation of Java Unit Testing & Code Refactoring at Capgemini',
    description:
        'Capgemini uses GenAI agents to automate unit test generation and refactoring for legacy Java code, significantly increasing test coverage and improving code quality.',
    url: 'https://www.youtube.com/watch?v=CEcUlnDpdW0',
    scores: createScores({
        detailedMetrics: 7.0,
        relevanceToUseCase: 9.0,
        reliabilityOfSource: 8.0,
        strategicAndTacticalInformation: 8.0,
        technicalImplementationDetails: 8.0,
        noVendorBias: 9.0,
        relevanceToClient: 7.0,
    }),
    publishedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000), // 12 days ago
    markdown: `# 1. Source of Information
 
 Source Kind: YouTube 
 Title: "How Gen AI agents can solve problem of java legacy code - Adam Witkowski | Tech3camp #112 Java"  
 Published At: 2024-08-28T14:48:08 
 Author: Infoshare 
 
 Quote: "hi guys uh I'm Adam. I'm an architect I'm working at Capgemini. and I'm running a group of enthusiasts um of both Java and gen so we create something called AI agents." 
 
 In this session, the presenter, Adam Witkowski, explains how his team at Capgemini exploits generative AI to tackle the challenges of legacy Java code. The presentation was a live session on YouTube during Tech3camp and forms the basis for this case study.
 
 # 2. Successes
 
 The project's main success is using Gen AI agents to automatically generate unit tests and refactor legacy Java code. This innovative technique has driven improvements in productivity by automating time-consuming and error-prone processes. By breaking down a massive codebase into its constituent classes and iteratively generating tests that compile, the system increases the test coverage significantly—from the abysmally low 3% often seen in legacy systems to an approximate 50% coverage in some cases.
 
 Quote: "once we have tests so phase one is creating tests … so now we dont no longer have 3% we have let's say 50." 
 
 Additional successful outcomes include:
 - Automated refactoring of methods which check for style issues (e.g., long methods, high cyclomatic complexity) – allowing code to be split into more manageable, well-named parts.
 - The creation of pull requests that encapsulate testing and refactoring results, thus streamlining code reviews and reducing the time to detect and correct errors.
 - The "document AI" agent that extracts a functional specification from the source code to generate human-friendly documentation. This provides valuable support especially for new joiners who need to navigate a previously incomprehensible codebase.
 
 Quote on documentation AI: "this agent … creates functional specification from sources so often our clients come to us and they say that they have like I don't know millions of lines in Java … it tells you what it's doing as an output." 
 
 # 3. Challenges
 
 The presentation also points out several challenges inherent in both legacy code maintenance and in the implementation of Gen AI solutions:
 
 ## Legacy code problems: 
 Legacy systems, especially those with millions of lines of code, are notoriously difficult to read and modify. With minimal test coverage initially, these systems inadvertently cause developers to spend most of their time reading rather than writing code, leading to brittle deployments that break in production.
 
 Quote: "a lot of studies show that people that developers spend 10 times more on reading than on writing and in Legacy this … proportion will be bigger." 
 
 ## Technical challenges: 
 Generating correct unit tests with generative AI isn't straightforward. The AI must overcome issues like context window limitations when processing large codebases, generate code that compiles on the first try, and subsequently adjust based on feedback from compilation errors and runtime test failures.
 
 Quote: "if we put a million of lines of code into CH GPT it wouldn't work … so usually if the problem is too big we just need to split it." 
 
 ## Iterative Error Correction: 
 The system relies on a self-correcting loop where the AI is given feedback (such as compilation or assertion errors) and then regenerates improved outputs. Although automated, this process requires careful engineering to maintain high quality without human intervention throughout the resubmission logic.
 
 Quote: "if it doesnt compile … I have a compilation error. right. so I get this compilation error and I feed it back to gen and say Hey ... and it says oh it's an import missing." 
 
 ## Adoption and integration challenges: 
 Some potential clients (notably in highly regulated sectors like financial services) are concerned about data privacy and the risk of sending code to external servers. The solution addresses this by running on private infrastructure, yet the challenge remains in ensuring all security and IP concerns are met.
 
 Quote: "there is completely zero problems with data privacy because we don't send it … it just runs on your laptop." 
 
 # 4. Technical Details
 
 At the heart of the approach is a clever design for incrementally generating unit tests and refactoring code using Gen AI agents. The technical design includes several key elements:
 
 ## Class-Level Breakdown: 
 The AI agent capitalizes on the natural modularity in Java—each class is processed separately so as to overcome limitations like the context window size of the language model.
 
 Quote: "every code base is already split into classes so we can just use this Division … for every class in a given Source just gen give me unit test." 
 
 ## Iterative Feedback Loop: 
 The AI first produces candidate unit tests; if these tests fail to compile or experience assertion errors, the error outputs are fed back to the AI agent to correct the tests. The loop continues until the tests meet certain quality criteria (e.g., execution time below 100 milliseconds, acceptable mutation scores).
 
 Quote: "if it doesn't compile I have a compilation error … and I feed it back to gen… but now let's say some assertion fails … then I get a newer version." 
 
 ## Automated Code Refactoring: 
 Beyond test generation, another AI agent focuses on code refactoring. Using static code metrics (like cyclomatic complexity and method length), the agent automatically suggests and implements changes to distribute long methods into smaller, more manageable units with meaningful names.
 
 Quote: "so we run our check style … for example gets output like uh a method is too long … and it gives us the same method with the same parameters just split into let's say three two methods." 
 
 ## Document Generation and Domain Abstraction: 
 A separate "document AI" agent converts technical source code—including modern constructs like hashmaps or linked lists—into human-friendly explanations and domain-specific descriptions. This documentation process helps bridge the gap for business analysts and new team members.
 
 Quote: "this agent … creates functional specification from sources … it tells you what it does as an output." 
 
 ## Private Infrastructure & Lightweight Setup: 
 The system is designed to run on private instances, ensuring that no code or sensitive data is sent offsite. It is also optimized to run on modest hardware (for example, on a laptop with GPU support), making the solution both cost-effective and secure.
 
 Quote: "it runs on my laptop … there is completely zero problems with data privacy because we don't send it." 
 
 # 5. Results
 
 The implementation of Gen AI agents has produced several measurable and qualitative improvements:
 
 ## Increased Test Coverage: 
 Legacy projects that started with extremely low test coverage (around 3%) now reach up to 50%. This better coverage facilitates safer application of automated code refactoring, thereby reducing the time and potential downtime associated with post-deployment fixes.
 
 Quote: "so now I don't have 3% we have let's say 50." 
 
 ## Enhanced Developer Productivity & Psychological Safety: 
 Developers previously working in fear of breaking fragile systems now receive immediate feedback through automated pull requests. This reduces the overhead of reverse-engineering bugs after weeks of delayed discovery, thereby affording the developers a safer and more confident development environment.
 
 Quote: "if you can imagine being a member of the team … they know something will break and they will not find out immediately … so they have more work … this can give them some kind of psychological safety." 
 
 ## Streamlined Code Review Process: 
 The approach inherently splits changes into distinct pull requests (one for test generation and another for refactoring). This clear delineation of duties simplifies code reviews and helps in identifying the root cause of potential issues (e.g., if a generated test unexpectedly fails, it may point at an undiscovered bug).
 
 Quote: "by Design there should be just two pull requests one pull request with tests and the other one with refactor code." 
 
 ## Potential for Discovering Hidden Bugs: 
 In some cases, a slightly less sophisticated generative system (e.g., a version analogous to ChatGPT-3.5) can inadvertently generate a failing test that actually exposes a bug in the legacy code. This emergent property adds another layer of value by flagging latent defects automatically.
 
 Quote: "if J can give you a Java test that fails and finds you a bug … it can have great great value." 
 
 # 6. Vendors Used
 
 While the presentation did not provide an exhaustive list of external vendors, a few key vendors and technologies are mentioned:
 
 ## Private AI Instance: 
 The solution leverages a "private AI" setup ensuring that code never leaves the client's secure environment.
  
 Quote: "This solution leverages private ai so nothing is sent to Internet." 
 
 ## Google Gemini: 
 The presentation briefly mentions running an "AMA with Google Gemma," suggesting an integration or comparison with Google's generative AI offerings, although details remain concise.
 
 Quote: "we Lun run something like AMA with Google Gemma too it's tiny." 
 
 ## Language Model Providers (implicit): 
 References to generative capabilities allude to using models akin to ChatGPT. The discussion about watermarking and adjusting for prompt responses suggests the system is designed to work with or alongside high-performance generative models.
 
 Quote: "if you can imagine that if J can give you a Java test … it will be in the code in the pull request." 
 
 # Conclusion
 
 The case study illustrates how leveraging Gen AI agents transforms the management of legacy Java code by automating unit test generation and code refactoring. Despite challenges such as handling compilation errors and ensuring performance security in highly regulated environments, the multi-agent system—comprising test generation, refactoring, and documentation engines—demonstrates pronounced successes. With tools running on modest hardware and integrated into private infrastructures, the solution enhances productivity, improves code quality, and provides developers with psychological safety against unexpected production issues.
 
 Each aspect, from technical feedback loops to enhancing developer confidence and achieving higher test coverage, is underpinned by the innovative application of generative AI applied at scale.`,
    references: [
        {
            id: '1',
            name: 'How Gen AI agents can solve problem of java legacy code',
            author: 'Adam Witkowski (Capgemini)',
            year: '2024',
            publisher: 'Infoshare (via YouTube)',
            url: 'https://www.youtube.com/watch?v=CEcUlnDpdW0',
        },
    ],
};
